import * as React from 'react';
import {useDispatch} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter/index';
import {usePageTitle} from 'src/contexts/page-title-context';
import {CustomBreadCrumbs} from 'src/components/common/custom-breadcrumbs';
import {useApiQuery} from 'src/hooks/useApiQuery';
import * as reduxApi from 'src/utils/redux-api-v2';
import {downloadCSV} from 'src/utils/download';
import {
  Table,
  BasicSingleCell,
  TableBottomBar,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  BodyMedium,
  TitleMedium,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {KPIBox} from '@spaced-out/ui-design-system/lib/components/KPIBox';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import moment from 'moment';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import type {
  OfferStageCandidateEntry,
  OfferStageCandidateExtras,
  OfferStageCandidateCountApiItem,
  OfferStageCandidateApiItem,
} from 'src/types/crm';
import css from './candidates-in-offer-stage.css';


const escapeCSVValue = (val: unknown): string => {
  const str = val == null ? '' : String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const PAGE_SIZE = 10;

const OFFER_STATUSES = [
  {
    status: 'GENERATE_OFFER',
    labelKey: 'Offers_to_be_generated',
    labelDefault: 'Offers to be generated',
  },
  {
    status: 'REQUEST_FOR_OFFER_APPROVAL',
    labelKey: 'Offers_to_be_sent_for_approval',
    labelDefault: 'Offers to be sent for approval',
  },
  {
    status: 'REQUESTED_FOR_APPROVAL',
    labelKey: 'Offer_approvals_pending',
    labelDefault: 'Offer approvals pending',
  },
  {
    status: 'SEND_OFFER_TO_CANDIDATE',
    labelKey: 'Offers_ready_to_be_rolled_out',
    labelDefault: 'Offers ready to be rolled out',
  },
  {
    status: 'OFFER_SENT_TO_CANDIDATE',
    labelKey: 'Offers_sent_to_candidates',
    labelDefault: 'Offers sent to candidates',
  },
  {
    status: 'OFFER_ACCEPTED_BY_CANDIDATE',
    labelKey: 'Offers_accepted_by_candidates',
    labelDefault: 'Offers accepted by candidates',
  },
  {
    status: 'OFFER_DECLINED_BY_CANDIDATE',
    labelKey: 'Offers_declined_by_candidates',
    labelDefault: 'Offers declined by candidates',
  },
  {
    status: 'OFFER_REVOKED',
    labelKey: 'Offers_Revoked',
    labelDefault: 'Offers Revoked',
  },
  {
    status: 'OFFER_EXPIRED',
    labelKey: 'Offer_Expired',
    labelDefault: 'Offer Expired',
  },
];

const CandidateNameCell = ({
  data,
  extras,
}: {
  data: OfferStageCandidateEntry;
  extras?: OfferStageCandidateExtras;
}) => (
  <BasicSingleCell>
    <Link
      onClick={() => extras?.onCandidateClick(data.jobId, data.candidateId)}
    >
      {data.candidateName}
    </Link>
  </BasicSingleCell>
);

const JobNameCell = ({data}: {data: OfferStageCandidateEntry}) => (
  <BasicSingleCell>
    <BodyMedium>{data.jobName}</BodyMedium>
  </BasicSingleCell>
);

const OfferStatusCell = ({data}: {data: OfferStageCandidateEntry}) => {
  const {t, makeKey} = useLabelTranslation();
  const offerStatus = OFFER_STATUSES.find((s) => s.status === data.status);
  const label = offerStatus
    ? t(makeKey(offerStatus.labelKey), offerStatus.labelDefault)
    : data.status;
  return (
    <BasicSingleCell>
      <BodyMedium>{label}</BodyMedium>
    </BasicSingleCell>
  );
};

const RecruiterCell = ({data}: {data: OfferStageCandidateEntry}) => (
  <BasicSingleCell>
    <BodyMedium>{data.recruiter || '-'}</BodyMedium>
  </BasicSingleCell>
);

const OfferCreatorCell = ({data}: {data: OfferStageCandidateEntry}) => (
  <BasicSingleCell>
    <BodyMedium>{data.offerCreator || '-'}</BodyMedium>
  </BasicSingleCell>
);

const LastActivityCell = ({data}: {data: OfferStageCandidateEntry}) => {
  const {language} = useLabelTranslation();
  if (!data.lastActivityOn) {
    return (
      <BasicSingleCell>
        <BodyMedium>-</BodyMedium>
      </BasicSingleCell>
    );
  }
  const relativeText = moment(data.lastActivityOn).locale(language).fromNow();
  const fullDate = moment(data.lastActivityOn).format(
    'dddd, MMMM D, YYYY h:mm A',
  );
  return (
    <BasicSingleCell>
      <Tooltip body={fullDate} placement="top">
        <BodyMedium>{relativeText}</BodyMedium>
      </Tooltip>
    </BasicSingleCell>
  );
};

export const CandidatesInOfferStage = () => {
  const {t, makeKey, language} = useLabelTranslation();
  const router = useHistory();
  const dispatch = useDispatch();
  const {
    actions: {setPageTitleProps},
  } = usePageTitle();

  const [page, setPage] = React.useState(0);
  const [activeStatuses, setActiveStatuses] = React.useState<string[]>([]);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const downloadCsv = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      const data = (await dispatch(
        reduxApi.post(
          '/api/dashboard/overview/offer-candidates',
          {
            teamIds: [],
            userIds: [],
            departmentId: 0,
            officeId: 0,
            dateRange: null,
            jobUserRoles: [],
            stageActionStatuses: [],
          },
          {page: 0, pageSize: 0},
          {apiPath: '/trmapi'},
        ),
      )) as OfferStageCandidateApiItem[];

      if (!Array.isArray(data) || data.length === 0) {
        return;
      }

      const headers = [
        t('Candidate_Name', 'Candidate Name'),
        t('Job_Title', 'Job Title'),
        t('Offer_Letter_Status', 'Offer Letter Status'),
        t('Recruiter', 'Recruiter'),
        t('Offer_Creator', 'Offer Creator'),
        t('Last_Activity', 'Last Activity'),
      ];

      const rows = data.map((item) => {
        const offerStatus = OFFER_STATUSES.find(
          (s) => s.status === item.status,
        );
        const statusLabel = offerStatus
          ? offerStatus.labelDefault
          : item.status ?? '';
        const lastActivity = item.lastActivityOn
          ? moment(item.lastActivityOn).format('YYYY-MM-DD HH:mm')
          : '';
        return [
          item.candidateName ?? '',
          item.jobName ?? '',
          statusLabel,
          item.recruiter ?? '',
          item.offerCreator ?? '',
          lastActivity,
        ]
          .map(escapeCSVValue)
          .join(',');
      });

      downloadCSV(
        [headers.map(escapeCSVValue).join(','), ...rows],
        'offered-candidates.csv',
      );
    } finally {
      setIsDownloading(false);
    }
  }, [dispatch, t]);

  React.useEffect(() => {
    setPageTitleProps({
      showBackButton: true,
      onBack: () => {
        router.replace('/dashboard');
      },
      tabSlotComponent: null,
      rightSlotComponent: null,
      customPageNameComponent: (
        <PageName>
          <TitleMedium>
            {t('Candidates_in_Offer_Stage', 'Candidates in Offer Stage')}
          </TitleMedium>
        </PageName>
      ),
    });
  }, [language]);

  const {data: countResult, isLoading: isCountLoading} = useApiQuery<
    OfferStageCandidateCountApiItem[]
  >(`/api/dashboard/overview/offer-candidates-data-count`, {
    apiOptions: {
      method: 'POST',
      data: {},
      options: {apiPath: '/trmapi'},
    },
  });

  const {data: result, isLoading} = useApiQuery<OfferStageCandidateApiItem[]>(
    `/api/dashboard/overview/offer-candidates?page=${page}&pageSize=${PAGE_SIZE}`,
    {
      queryOptions: {
        queryKey: ['offer-candidates-list', page, activeStatuses],
      },
      apiOptions: {
        method: 'POST',
        data: {
          teamIds: [],
          userIds: [],
          departmentId: 0,
          officeId: 0,
          dateRange: null,
          jobUserRoles: [],
          stageActionStatuses: activeStatuses,
        },
        options: {apiPath: '/trmapi'},
      },
    },
  );

  const statusCounts: Record<string, number> = React.useMemo(() => {
    if (!Array.isArray(countResult)) {
      return {};
    }
    return countResult.reduce<Record<string, number>>((acc, item) => {
      acc[item.stepCandidateStatus ?? ''] = item.count ?? 0;
      return acc;
    }, {});
  }, [countResult]);

  const totalCount = React.useMemo(
    () => Object.values(statusCounts).reduce((sum, n) => sum + n, 0),
    [statusCounts],
  );

  const entries: OfferStageCandidateEntry[] = React.useMemo(() => {
    if (!Array.isArray(result)) {
      return [];
    }
    return result.map((item) => ({
      id: `${item.candidateId}-${item.jobId}-${item.stageCandidateId ?? ''}`,
      candidateId: String(item.candidateId ?? ''),
      candidateName: item.candidateName ?? '',
      jobId: String(item.jobId ?? ''),
      jobName: item.jobName ?? '',
      status: item.status ?? '',
      recruiter: item.recruiter ?? '',
      offerCreator: item.offerCreator ?? '',
      lastActivityOn: item.lastActivityOn ?? null,
    }));
  }, [result]);

  const filteredTotal = React.useMemo(
    () =>
      activeStatuses.length > 0
        ? activeStatuses.reduce((sum, s) => sum + (statusCounts[s] ?? 0), 0)
        : totalCount,
    [activeStatuses, statusCounts, totalCount],
  );
  const hasNextPage = (page + 1) * PAGE_SIZE < filteredTotal;

  const headers = [
    {
      label: t('Candidate_Name', 'Candidate Name'),
      key: 'candidateName' as keyof OfferStageCandidateEntry,
      render: CandidateNameCell,
    },
    {
      label: t('Job_Title', 'Job Title'),
      key: 'jobName' as keyof OfferStageCandidateEntry,
      render: JobNameCell,
    },
    {
      label: t('Offer_Letter_Status', 'Offer Letter Status'),
      key: 'status' as keyof OfferStageCandidateEntry,
      render: OfferStatusCell,
    },
    {
      label: t('Recruiter', 'Recruiter'),
      key: 'recruiter' as keyof OfferStageCandidateEntry,
      render: RecruiterCell,
    },
    {
      label: t('Offer_Creator', 'Offer Creator'),
      key: 'offerCreator' as keyof OfferStageCandidateEntry,
      render: OfferCreatorCell,
    },
    {
      label: t('Last_Activity', 'Last Activity'),
      key: 'lastActivityOn' as keyof OfferStageCandidateEntry,
      render: LastActivityCell,
    },
  ];

  const extras: OfferStageCandidateExtras = {
    onCandidateClick: (jobId, candidateId) => {
      router.push(
        `/crm/job/${jobId}/info/applicants?applicantId=${candidateId}`,
      );
    },
  };

  const breadCrumbLinks = [
    {
      name: t('Dashboard', 'Dashboard'),
      isActive: false,
      onClick: () => router.push('/dashboard'),
    },
    {
      name: t('Candidates_in_Offer_Stage', 'Candidates in Offer Stage'),
      isActive: true,
      onClick: () => router.push('/dashboard/offer-candidates'),
    },
  ];

  const toggleStatus = (status: string) => {
    setPage(0);
    setActiveStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  return (
    <>
      <CustomBreadCrumbs breadCrumbLinks={breadCrumbLinks} />
      <div className={css.container}>
        <div className={css.titleRow}>
          <SubTitleLarge>
            {`${totalCount} ${t(
              'Candidates_in_Offer_Stage',
              'Candidates in Offer Stage',
            )}`}
          </SubTitleLarge>
          <Button
            type="secondary"
            size="small"
            iconLeftName="arrow-to-bottom"
            isLoading={isDownloading}
            onClick={downloadCsv}
          >
            {t('Download_full_report', 'Download full report')}
          </Button>
        </div>

        <div className={css.kpiSection}>
          <div className={css.kpiGrid}>
            {OFFER_STATUSES.map(({status, labelKey, labelDefault}) => (
              <KPIBox
                key={status}
                topContent={t(makeKey(labelKey), labelDefault)}
                middleContent={
                  isCountLoading ? (
                    <Shimmer width={'100px'} height={'32px'} />
                  ) : (
                    String(statusCounts[status] ?? 0)
                  )
                }
                onClick={() => toggleStatus(status)}
                classNames={{
                  wrapper: activeStatuses.includes(status)
                    ? css.kpiCardActive
                    : undefined,
                }}
              />
            ))}
          </div>
        </div>

        <div className={css.tableContainer}>
          {/* <TableTopBar>
          <BodyMedium>
            {t('Candidates_in_Offer_Stage', 'Candidates in Offer Stage')}
          </BodyMedium>
        </TableTopBar> */}
          <Table
            entries={entries}
            headers={headers}
            extras={extras}
            idName="id"
            borderRadius={'0'}
            isLoading={isLoading}
            stickyHeader
            emptyText={
              <EmptyState
                title={t('No_candidates_found', 'No candidates found')}
                description={t(
                  'There_are_no_candidates_in_the_offer_stage',
                  'There are no candidates in the offer stage.',
                )}
                imageVariant="file"
              />
            }
          />
          <TableBottomBar>
            <div className={css.paginationControls}>
              <Button
                type="ghost"
                size="small"
                iconLeftName="angle-left"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              />
              <BodyMedium>{page + 1}</BodyMedium>
              <Button
                type="ghost"
                size="small"
                iconRightName="angle-right"
                disabled={!hasNextPage}
                onClick={() => {
                  if (hasNextPage) {
                    setPage((p) => p + 1);
                  }
                }}
              />
            </div>
          </TableBottomBar>
        </div>
      </div>
    </>
  );
};
